<template>
  <v-container v-if="$can('browse', 'ServiceCostReport')">
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Service Cost Reports
        </div>
      </v-col>
      <v-col
        v-if="$can('create', 'ServiceCostReport')"
        class="shrink"
      >
        <v-btn
          color="primary"
          class="ma-0"
          @click="serviceCostReportCreateDialog = true"
        >
          <v-icon left>
            add
          </v-icon>
          Create New Report
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        sm="3"
        class="hidden-xs-only"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <yacht-modal-select
            v-model="filtersDirty.yacht_id"
            :dialog-visible.sync="yachtModalSelectVisible"
            label="Yacht"
            solo
            hide-details
            clearable
            class="mb-2"
            @submit="yachtModalSelectVisible = false"
          />
        </div>
      </v-col>
      <v-col
        sm="13"
        cols="16"
      >
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="serviceCostReports.meta.from"
          :to="serviceCostReports.meta.to"
          :total="serviceCostReports.meta.total"
          :last-page="serviceCostReports.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="serviceCostReports.data"
          :loading="serviceCostReports.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="serviceCostReports.meta.total"
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item: report }">
            <tr>
              <td>
                <a
                  href="#"
                  @click.prevent="editServiceCostReport.id = report.id; editServiceCostReport.visible = true"
                >
                  {{ report.yacht.name }}
                </a>
              </td>
              <td class="grey--text">
                {{ report.staff.name }}
              </td>
              <td class="grey--text">
                {{ report.created_at | moment('D MMM YYYY') }}
              </td>
              <td>
                {{ report.date_from | moment('D MMM YYYY') }}
              </td>
              <td>
                {{ report.date_to | moment('D MMM YYYY') }}
              </td>
              <td>
                <div
                  v-if="report.price !== report.original_price"
                  class="error--text text-no-wrap"
                  style="text-decoration: line-through"
                >
                  {{ report.original_price | currency(report.currency) }}
                </div>
                <div class="text-no-wrap">
                  {{ report.price | currency(report.currency) }}
                </div>
              </td>
              <td class="text-no-wrap">
                <v-btn
                  small
                  rounded
                  text
                  color="grey"
                  target="_blank"
                  :to="{ name: 'serviceCostReportPrint', params: { id: report.id } }"
                >
                  <v-icon
                    left
                    small
                  >
                    print
                  </v-icon>
                  Print
                </v-btn>
                <v-btn
                  small
                  rounded
                  text
                  color="grey"
                  @click="editServiceCostReport.id = report.id; editServiceCostReport.visible = true"
                >
                  <v-icon x-small>
                    fa fa-pen
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <div
              v-if="isFiltered"
              class="text-center py-4"
            >
              <div>
                There are no results for your search.
              </div>
              <div
                class="mt-2"
              >
                <v-btn
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </div>
            </div>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <div
              v-else
              class="text-center py-4"
            >
              There are no service cost reports at the moment.
            </div>
            <!--</editor-fold>-->
          </template>
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <service-cost-report-create-dialog
      :visible.sync="serviceCostReportCreateDialog"
      @service-cost-report:created="serviceCostReportCreated"
    />

    <service-cost-report-manage-items-dialog
      :service-cost-report-id="editServiceCostReport.id"
      :visible.sync="editServiceCostReport.visible"
      @updated="fetchServiceCostReports()"
      @report:deleted="fetchServiceCostReports()"
    />
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
import ServiceCostReportCreateDialog from '@/components/service/serviceCostReports/ServiceCostReportCreateDialog'
import YachtModalSelect from '@/components/service/helpers/YachtModalSelect'
import ServiceCostReport from '@/models/ServiceCostReport'
import ServiceCostReportManageItemsDialog
  from '@/components/service/serviceCostReports/ServiceCostReportManageItemsDialog'

export default {
    name: 'InventoryItemsIndex',
    components: {
      ServiceCostReportManageItemsDialog,
      YachtModalSelect,
      ServiceCostReportCreateDialog
    },
    mixins: [paginationMixin, filterableMixin, serviceCurrentLocationMixin],
    data() {
      return {
        serviceCostReports: {
          data: [],
          meta: {},
          loading: false
        },
        headers: [
          { text: 'Yacht', value: 'yacht.name', sortable: false },
          { text: 'Created By', value: 'staff_id', sortable: false },
          { text: 'Created At', value: 'created_at', sortable: true, width: '1%' },
          { text: 'From', value: 'date_from', sortable: false, width: '1%' },
          { text: 'To', value: 'date_to', sortable: false, width: '1%' },
          { text: 'Price', value: 'price', sortable: false, width: '1%' },
          { sortable: false, width: '1%' }
        ],
        filtersDirty: cloneDeep(ServiceCostReport.emptyFilters),
        filtersDialog: false,
        yachtModalSelectVisible: false,
        serviceCostReportCreateDialog: false,
        editServiceCostReport: {
          visible: false,
          id: null
        }
      }
    },
    computed: {
      isFiltered() {
        return !!this.filtersDirty.yacht_id
      }
    },
    created() {
      this.fetchServiceCostReports()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchServiceCostReports()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      onCurrentLocationUpdate() {
        this.resetPagination()
      },
      async fetchServiceCostReports() {
        this.serviceCostReports.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          ...this.currentLocationQueryParams,
          yacht_id: this.filtersDirty.yacht_id ? this.filtersDirty.yacht_id.id : undefined
        }

        try {
          let result = await ServiceCostReport.api.fetch(queryParams)

          this.serviceCostReports.data = result.data
          this.serviceCostReports.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.serviceCostReports.loading = false
        }
      },
      serviceCostReportCreated(report) {
        this.serviceCostReports.data.unshift({ ...report, new: true })
      }
    }
  }
</script>
